import type { ObjectGraph } from '@jet/environment/dependencies';
import { ClassicalAPIURL } from './ClassicalAPIURL';
import type { ClassicalAPIResponse } from './types';
import { requestClassicalAPIResource } from '../utilities/requestClassicalAPIResource';
import { Bag } from '../dependencies/bag';
import type { SortParams } from '../intents/browse-page/types';

export async function requestEntitiesList(
    entitiesPath: string,
    objectGraph: ObjectGraph,
    defaultParams: SortParams = {},
): Promise<ClassicalAPIResponse> {
    const countryCode = Bag.countryCode(objectGraph);
    if (!entitiesPath) {
        throw new Error('requestEntitiesList: entitiesPath is required');
    }

    const { path, params } = extractPathAndParams(entitiesPath);

    const url = new ClassicalAPIURL(
        `/query/view/${countryCode}/${path}`,
        objectGraph,
    );

    url.queryString.addParameters({
        // Need to perform an object merge otherwise duplicate
        // parameters will be added to the URL string
        ...{ ...defaultParams, ...params },
        page: params?.page?.toString() || '0',
    });

    return requestClassicalAPIResource({ url: url.string }, objectGraph);
}

// Function to extract the API Path for specific Classical Action URL & also extract query params
function extractPathAndParams(url: string) {
    // Updated regex to match `/query/view/` followed by 2 or 3 letters as country code and capture the path after it
    const regex = /\/query\/view\/[a-z]{2,3}\/(.*?)(\?|$)/;
    const match = url.match(regex);

    // Extract the path after the country code
    const path = match ? match[1] : url; // Default to the original path sent, if /query/view/ is not present

    // Extract the query parameters
    const queryParams = new URLSearchParams(url.split('?')[1]);
    const params: { [key: string]: string } = {};

    queryParams.forEach((value, key) => {
        params[key] = value;
    });

    return {
        path,
        params,
    };
}
